import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import expenseService from '@api/v1/expenseService'
import LoadingSpinner from '@components/common/LoadingSpinner'
import ExpenseForm from '@components/billing/ExpenseForm'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'

const Expenses = () => {
  useDocumentTitle('Expenses')
  const queryClient = useQueryClient()
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'))
  const [showCreate, setShowCreate] = useState(false)

  /*
   * CC-53132 — `isLoading` cannot tell "no expenses" from "we never asked".
   *
   * react-query defines `isLoading` as `isPending && isFetching`, so it is FALSE
   * for a query that is pending but NOT in flight. The way that happens in the
   * field is a PAUSED fetch: with the default `networkMode: 'online'` react-query
   * refuses to fire while `onlineManager` believes the browser is offline, so
   * `queryFn` is never called, nothing throws, nothing is logged anywhere on
   * either side, and `data` stays `undefined`.
   *
   * This page then rendered BOTH of its answers off that undefined: the subtitle
   * read "0 expenses — $0.00" and the panel read "No expenses found between
   * <from> and <to>". A named date range makes it worse, not better — it reads as
   * a considered answer about a specific period, which is exactly what a
   * timekeeper checking whether a disbursement was ever entered would rely on.
   *
   * `useReadState` (not the bare `readState`) because it carries the permanent
   * blocked-read diagnostic: a paused read produces no request to find in a log,
   * so that console warning is the only evidence this state ever leaves behind.
   */
  const expensesQuery = useQuery({
    queryKey: ['expenses', startDate, endDate],
    queryFn: () => expenseService.getExpenses({ startDate, endDate }),
  })
  const expensesState = useReadState('expenses', expensesQuery)
  const expensesReady = expensesState === 'ready'
  const { data, error, refetch } = expensesQuery

  const saveMutation = useMutation({
    mutationFn: (expense) => expenseService.saveExpense(expense),
    onSuccess: () => {
      setShowCreate(false)
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
    },
  })

  /*
   * `data?.expenses` is left able to be `undefined` — the `[]` fallback that used
   * to be here is what let an unfinished read be counted and summed. Both values
   * below are read ONLY inside `expensesReady` branches, so the array coercion
   * here is shape normalisation for a completed read, never a stand-in for one.
   */
  const expenses = Array.isArray(data?.expenses) ? data.expenses : []
  const totalAmount = expenses.reduce(
    (sum, e) => sum + (e.billableValue || 0), 0
  )

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display text-gray-900">Expenses</h1>
          {/* Gated on a COMPLETED read. This line carries a count AND a money
              total, and both were computed from `undefined` while the read was
              unresolved — so a paused read printed "0 expenses — $0.00" beside the
              page title, which is a statement about the firm's disbursements and
              not about the network. Nothing is rendered in its place: a zero here
              is either an observation or a lie, and there is no third form of it
              worth showing. */}
          {expensesReady && (
            <p className="text-sm text-gray-500 mt-1">
              {expenses.length} expense{expenses.length !== 1 ? 's' : ''}
              {' '}&mdash; ${totalAmount.toFixed(2)}
            </p>
          )}
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2.5 bg-cb-accent text-white rounded-lg text-sm
            font-medium hover:bg-cb-accent/90 transition-colors shadow-sm"
        >
          + New Expense
        </button>
      </div>

      {/* Create Panel */}
      {showCreate && (
        <div className="mb-6 bg-white rounded-xl shadow-sm border
          border-gray-100 p-5 max-w-xl"
        >
          <h2 className="text-lg font-display text-gray-900 mb-4">
            New Expense
          </h2>
          {saveMutation.isError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100
              rounded-lg text-sm text-red-700"
            >
              {saveMutation.error?.message || 'Failed to save expense'}
            </div>
          )}
          <ExpenseForm
            onSave={(data) => saveMutation.mutate(data)}
            onCancel={() => setShowCreate(false)}
            saving={saveMutation.isPending}
          />
        </div>
      )}

      {/* Date Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500">From</label>
          <input
            type="date"
            aria-label="Start date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-lg
              text-sm text-gray-700 focus:outline-none focus:ring-2
              focus:ring-cb-accent/30 focus:border-cb-accent"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500">To</label>
          <input
            type="date"
            aria-label="End date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-200 rounded-lg
              text-sm text-gray-700 focus:outline-none focus:ring-2
              focus:ring-cb-accent/30 focus:border-cb-accent"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100
        overflow-hidden"
      >
        {/* `idle` rides with `loading` deliberately: this query has no `enabled`
            option, so it cannot reach that state today. It is folded in anyway
            because every branch below is gated on a NAMED state, and an unhandled
            fifth one falls through all of them and renders nothing at all — the
            catalog fix shipped exactly that blank panel once already. */}
        {expensesState === 'loading' || expensesState === 'idle' ? (
          <div className="p-12 flex justify-center">
            <LoadingSpinner message="Loading expenses..." />
          </div>
        ) : expensesState === 'blocked' ? (
          /* The state that used to render as "No expenses found between <from>
             and <to>". No request was sent, so nothing is known about that date
             range. A spinner forever would be almost as misleading, so name what
             happened and offer the retry rather than a page reload. */
          <div role="alert" className="p-6 text-sm text-amber-900 bg-amber-50">
            <p className="font-medium">Can’t reach the server, so we haven’t loaded your expenses.</p>
            <p className="mt-1 text-amber-800">
              This is not an empty date range — your connection looks offline, so nothing was
              read for {dayjs(startDate).format('MMM D, YYYY')} to {dayjs(endDate).format('MMM D, YYYY')}.
              Check it and try again.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              Try again
            </button>
          </div>
        ) : expensesState === 'failed' ? (
          /* Kept the original sentence, which was already right. What it lacked
             was `role="alert"` (so it is announced rather than silently replacing
             a table) and a way to ask again short of reloading the app. */
          <div className="p-6 text-sm text-red-700 bg-red-50" role="alert">
            <p className="font-medium">
              Failed to load expenses: {error.message}
            </p>
            <p className="mt-1">This is not an empty date range — nothing was read.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
            >
              Try again
            </button>
          </div>
        ) : expenses.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex
              items-center justify-center mx-auto mb-3"
            >
              <svg
                className="w-6 h-6 text-gray-300" fill="none"
                viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round" strokeLinejoin="round"
                  d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198
                  1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0
                  013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25
                  6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621
                  0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125
                  1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0
                  0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5
                  1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3
                  0 016 0zm3 0h.008v.008H18V10.5zm-12
                  0h.008v.008H6V10.5z"
                />
              </svg>
            </div>
            <p className="text-sm text-gray-500">
              No expenses found{startDate || endDate
                ? ` between ${startDate ? dayjs(startDate).format('MMM D, YYYY') : 'start'} and ${endDate ? dayjs(endDate).format('MMM D, YYYY') : 'now'}`
                : ''}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3 text-xs font-semibold
                  text-gray-500 uppercase tracking-wider">Date</th>
                <th className="text-left px-5 py-3 text-xs font-semibold
                  text-gray-500 uppercase tracking-wider">Matter</th>
                <th className="text-left px-5 py-3 text-xs font-semibold
                  text-gray-500 uppercase tracking-wider">Description</th>
                <th className="text-left px-5 py-3 text-xs font-semibold
                  text-gray-500 uppercase tracking-wider">Code</th>
                <th className="text-right px-5 py-3 text-xs font-semibold
                  text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="text-center px-5 py-3 text-xs font-semibold
                  text-gray-500 uppercase tracking-wider">Billable</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {expenses.map((expense, i) => (
                <tr
                  key={expense.id || i}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-5 py-3.5">
                    <p className="text-sm text-gray-600">
                      {expense.date
                        ? dayjs(expense.date).format('MMM D')
                        : '--'}
                    </p>
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-sm font-medium text-gray-900">
                      {expense.matterName || '--'}
                    </p>
                  </td>
                  <td className="px-5 py-3.5 max-w-xs">
                    <p className="text-sm text-gray-600 truncate">
                      {expense.description || expense.name || '--'}
                    </p>
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-sm text-gray-600">
                      {expense.billingCode || '--'}
                    </p>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {expense.billableValue > 0
                        ? `$${expense.billableValue.toFixed(2)}`
                        : '--'}
                    </p>
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${
                      expense.isNonBillable
                        ? 'bg-gray-300'
                        : 'bg-cb-green'
                    }`} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 bg-gray-50/50">
                <td colSpan={4} className="px-5 py-3">
                  <p className="text-sm font-semibold text-gray-700">Total</p>
                </td>
                <td className="px-5 py-3 text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    ${totalAmount.toFixed(2)}
                  </p>
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  )
}

export default Expenses
